/**
 * Global error polyfill to handle cases where code throws non-Error objects.
 * This prevents crashes in error boundaries when empty objects or other non-Error values are thrown.
 */

// Store the original Promise.reject to patch it
const originalPromiseReject = Promise.reject;

// Override Promise.reject to ensure all rejections are Error objects
Promise.reject = function(reason: any) {
  // If the reason is already an Error, use it as-is
  if (reason instanceof Error) {
    return originalPromiseReject.call(this, reason);
  }
  
  // Convert non-Error objects to proper Error instances
  const errorMessage = typeof reason === 'string' ? reason :
                      reason?.message || reason?.detail ||
                      'Promise rejected with non-Error object';
  
  // Log the original reason for debugging
  if (reason && typeof reason === 'object' && Object.keys(reason).length === 0) {
    console.warn('Promise rejected with empty object, converting to Error:', reason);
  }
  
  return originalPromiseReject.call(this, new Error(errorMessage));
};

// Also patch window.onerror to handle global unhandled errors
const originalOnError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  // If error is not an Error instance, create one
  if (error && !(error instanceof Error)) {
    const err = error as any;
    const errorMessage = typeof err === 'string' ? err :
                        err?.message || err?.detail ||
                        `Non-Error thrown: ${JSON.stringify(err)}`;
    error = new Error(errorMessage);
  }
  
  // Call the original handler if it exists
  if (originalOnError) {
    return originalOnError.call(this, message, source, lineno, colno, error);
  }
  
  return false;
};

// Patch unhandled promise rejections
window.addEventListener('unhandledpromiserejection', function(event: PromiseRejectionEvent) {
  // If the reason is not an Error, convert it
  if (event.reason && !(event.reason instanceof Error)) {
    const errorMessage = typeof event.reason === 'string' ? event.reason :
                        event.reason?.message || event.reason?.detail ||
                        'Unhandled promise rejection with non-Error';
    
    console.warn('Unhandled promise rejection with non-Error object, converting:', event.reason);
    
    // Create a proper Error and update the event reason
    (event as any).reason = new Error(errorMessage);
  }
} as EventListener);

export {}; // Make this a module
